import React, { useRef } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  InputAdornment,
  IconButton,
  withStyles,
  OutlinedInput,
} from "@material-ui/core";
import { Search as SearchIcon, Clear as ClearIcon } from "@material-ui/icons";

const SearchField = withStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    height: 60,
    borderTopLeftRadius: theme.shape.borderRadius,
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  input: {
    "&::-ms-clear": {
      display: "none",
    },
  },
  notchedOutline: {
    borderColor: theme.palette.text.primary,
    borderRight: "none",
  },
}))(OutlinedInput);

const SubmitButton = withStyles((theme) => ({
  root: {
    background: theme.palette.primary.main,
    "&:hover": { background: theme.palette.primary.dark },
    color: theme.palette.primary.contrastText,
    height: "100%",
    width: 72,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderTopRightRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
  },
}))(IconButton);

interface Props {
  onSubmit: (values: Record<string, any>) => void;
  defaultValue: string;
}
const PLACEHOLDER_TEXT =
  "Search by account no, phone number, email, postcode, MPAN/MPRN";

export const SearchForm: React.FC<Props> = ({ onSubmit, defaultValue }) => {
  const inputRef = useRef<HTMLInputElement>();

  const { register, setValue, watch, handleSubmit } = useForm({
    defaultValues: { query: defaultValue },
  });

  const handleClear = () => {
    setValue("query", "");
    // Fire the search so the clear action results in an API call
    handleSubmit(onSubmit)();
    // Since we're removing the ClearButton element, we place focus back on the Input itself
    inputRef.current?.focus();
  };

  // derive a boolean that tells us if there is a value in the text field
  const v = watch("query", "");
  const isFilled = v.length > 0;

  return (
    <form data-testid="SearchForm" noValidate onSubmit={handleSubmit(onSubmit)}>
      <Box my={3} maxWidth={760} display="flex">
        <Box flex="1 1 auto">
          <SearchField
            data-testid="SearchForm__Field"
            inputRef={(node) => {
              register(node); // register with the form
              inputRef.current = node; // register with our ref to place focus
            }}
            inputProps={{
              "data-testid": "SearchForm__Input",
              autoComplete: "off",
            }}
            name="query"
            placeholder={PLACEHOLDER_TEXT}
            fullWidth
            endAdornment={
              isFilled ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClear}
                    data-testid="SearchForm__Button--clear"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
          />
        </Box>
        <Box flex="0 0 auto">
          <SubmitButton data-testid="SearchForm__Button--submit" type="submit">
            <SearchIcon fontSize="large" />
          </SubmitButton>
        </Box>
      </Box>
    </form>
  );
};
